import React, { useMemo } from 'react';
import { Badge } from 'reactstrap';
import './RelatedLogsTimeline.scss';

const relationConfig = {
    resource: { color: 'info', label: 'Resource', icon: 'fa-database' },
    session: { color: 'success', label: 'Session', icon: 'fa-clock' },
    impersonation: { color: 'purple', label: 'Impersonation', icon: 'fa-user-secret' },
    current: { color: 'primary', label: 'Current', icon: 'fa-circle' }
};

const formatTime = (date) => {
    return new Date(date).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const RelatedLogsTimeline = ({ relatedLogs, currentLogId, onSelectLog }) => {
    const { sameResource = [], sameSession = [], impersonationChain = [] } = relatedLogs || {};

    // Merge and sort all related logs chronologically
    const timelineItems = useMemo(() => {
        const allLogs = [
            ...sameResource,
            ...sameSession,
            ...impersonationChain
        ];

        // Deduplicate by _id (a log might appear in multiple relation types)
        const uniqueLogs = allLogs.reduce((acc, log) => {
            const id = log._id.toString();
            if (!acc[id]) {
                acc[id] = log;
            } else if (log.relationType === 'impersonation') {
                // Prioritize impersonation type over others
                acc[id] = log;
            }
            return acc;
        }, {});

        return Object.values(uniqueLogs).sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
    }, [sameResource, sameSession, impersonationChain]);

    if (timelineItems.length === 0) {
        return (
            <div className="related-logs-empty">
                <span className="fas fa-stream text-white-50 mb-2" style={{ fontSize: '2rem' }} />
                <p className="text-white-50 mb-0">No related activity found</p>
            </div>
        );
    }

    return (
        <div className="related-logs-timeline">
            {timelineItems.map((log) => {
                const isCurrent = log._id === currentLogId;
                const relationType = isCurrent ? 'current' : log.relationType;
                const config = relationConfig[relationType] || relationConfig.session;

                return (
                    <div
                        key={log._id}
                        className={`timeline-item ${relationType} ${isCurrent ? 'is-current' : ''}`}
                        onClick={() => !isCurrent && onSelectLog && onSelectLog(log._id)}
                        role={isCurrent ? undefined : 'button'}
                        tabIndex={isCurrent ? undefined : 0}
                    >
                        <div className="timeline-dot">
                            <span className={`fas ${config.icon}`} />
                        </div>
                        <div className="timeline-content">
                            <div className="timeline-header">
                                <span className="timeline-time">{formatTime(log.createdAt)}</span>
                                <Badge
                                    color={config.color === 'purple' ? undefined : config.color}
                                    className={config.color === 'purple' ? 'bg-purple' : ''}
                                    style={{ borderRadius: 0, fontSize: '0.7rem' }}
                                >
                                    {log.action?.toLowerCase().replace(/_/g, ' ')}
                                </Badge>
                            </div>
                            <div className="timeline-details">
                                {log.details || `${log.entity} ${log.action?.toLowerCase()}`}
                            </div>
                            {log.user && (
                                <div className="timeline-user">
                                    <span className="fas fa-user fa-xs me-1" />
                                    {log.user.firstName} {log.user.lastName}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default RelatedLogsTimeline;
